/**
 * World module: the world, its component registry, the TransformSystem, and
 * the entity commands (registered with the writer closed over, review F-1).
 *
 * The one write capability that leaves this module is `levels.commit`, which
 * swaps in a fully prepared world. Only the Editor's `level.open` command
 * calls it, so level loads also go through the Command Bus.
 */
import type { LevelManifest } from "../assets/content";
import { CommandsModule } from "../commands/CommandsModule";
import { CoreModule } from "../core/CoreModule";
import { defineModule } from "../core/module";
import { entityCommands } from "./commands";
import { createV1ComponentRegistry } from "./components";
import { prepareWorld } from "./serialization";
import type { PreparedWorld } from "./serialization";
import { TransformSystem } from "./TransformSystem";
import { World } from "./World";
import type { WorldData } from "./World";

export interface LevelCommitter {
  /** Builds a detached world from a resolved manifest. Throws LevelFormatError on structural problems. */
  prepare(manifest: LevelManifest): PreparedWorld;
  /** Swaps the prepared world in. Call only from `level.open` (inside a command). */
  commit(data: WorldData): void;
}

export const WorldModule = defineModule({
  name: "World",
  dependsOn: [CoreModule, CommandsModule],
  init: ({ deps: { Core, Commands } }) => {
    const components = createV1ComponentRegistry();

    const world = new World(Core.scheduler);
    Commands.bus.register(
      entityCommands({ writer: world.writer, reader: world.reader, components }),
    );

    const levels: LevelCommitter = {
      prepare: (manifest) => prepareWorld(manifest, components),
      commit: (data) => {
        world.writer.replace(data);
      },
    };

    return {
      world: world.reader,
      components,
      transforms: new TransformSystem(world.reader),
      levels,
    };
  },
  shutdown: ({ transforms }) => {
    transforms.dispose();
  },
});
